import { searchKey } from './search-key'
import { queryOptions } from '@w/react-query/db'
import { sql } from '@w/db'
import { s } from '@w/schema'
import type { Tel } from '@w/tel'
import type { ClientDb } from '../db/db'
import { usablePage } from '../db/page-eligibility'
import { embeddedKinds } from '../server/db/page-kind-policy'
import { encodeEmbedding, type PageEmbeddingIndex } from './page-embeddings'
import { FLASHCARDS_EMBEDDING_INDEX } from '../db/embedding-schema'
import { encodeEmbeddingVector, NEMOTRON_EMBEDDING_INDEX } from './embedding-protocol'
import { refreshPageSearch } from '../db/page-search-projection'

// Module scope (Zod: define schemas once): inline schemas paid construction on every call.
const scoredPageSchema = s.object({ pageId: s.string(), title: s.string(), score: s.number() })

export type PageSearchMode = 'fulltext' | 'vector' | 'hybrid'
export type PageSearchOptions = { mode?: PageSearchMode; limit?: number }
export type PageSearchEmbeddings = { index?: PageEmbeddingIndex; embedQuery(text: string, tel: Tel): Promise<number[]>; embedNemotronQuery?(text: string, tel: Tel): Promise<number[]> }
export type PageSearchResult = { pageId: string; title: string; score: number }

export async function searchPages(args: {
  db: ClientDb
  workspaceId: string
  query: string
  embeddings: PageSearchEmbeddings
  tel: Tel
  options?: PageSearchOptions
}): Promise<(PageSearchResult & { spaceId: string })[]> {
  // One-shot readers (fcc search, server tools) have no open-file drain (startPageSearchRefresh): each read drains the
  // queue first (one EXISTS query when empty), after query embedding has started. The app's live search
  // (pageSearchOptions) stays one query.
  const stages = await preparePageSearch({ query: args.query, embeddings: args.embeddings, tel: args.tel, ...args.options },
    async prepared => {
      await refreshPageSearch(args.db)
      return pageSearchOptions({ db: args.db, ...prepared }).queryFn({ tel: args.tel })
    })
  const rows = await stages.reranked ?? stages.local
  return rows.map(row => Object.assign({}, row, { spaceId: args.workspaceId }))
}

/** One preparation/lifecycle for single-file execution and per-space query fan-out. */
export async function preparePageSearch<T>(
  input: { query: string; embeddings: PageSearchEmbeddings; tel: Tel } & PageSearchOptions,
  run: (prepared: Omit<Parameters<typeof pageSearchOptions>[0], 'db'>) => T | Promise<T>,
) {
  let mode = input.mode ?? 'hybrid'
  // Capture model identity before inference: a later configuration must never
  // route an old query vector into the replacement model's index.
  const index = input.embeddings.index ?? FLASHCARDS_EMBEDDING_INDEX
  let vector: Uint8Array<ArrayBuffer> | undefined
  const nemotron = startNemotronQuery(input.embeddings, input.query, mode, input.tel)
  if (mode !== 'fulltext') {
    try {
      vector = encodeEmbedding(await input.embeddings.embedQuery(input.query, input.tel), 'query', index.dimensions)
    } catch (error) {
      if (input.mode !== undefined) throw error
      mode = 'fulltext'
    }
  }
  const prepared = { query: input.query, mode, vector, index, limit: input.limit ?? 10 }
  const local = await run(prepared)
  const reranked = mode === 'fulltext' ? Promise.resolve()
    : nemotron.then(nemotronVector => nemotronVector ? run({ ...prepared, nemotronVector }) : undefined)
  return { local, reranked }
}

/** Start at t0; failure is optional and never changes the local search mode. */
async function startNemotronQuery(embeddings: PageSearchEmbeddings, query: string, mode: PageSearchMode, tel: Tel): Promise<number[] | null> {
  if (mode === 'fulltext' || !embeddings.embedNemotronQuery) return null
  try {
    const vector = await embeddings.embedNemotronQuery(query, tel)
    encodeEmbeddingVector(vector)
    return vector
  } catch { return null }
}

export function pageSearchOptions(args: {
  spaceId?: string
  db: ClientDb
  query: string
  mode: PageSearchMode
  vector?: Uint8Array<ArrayBuffer>
  index?: PageEmbeddingIndex
  nemotronVector?: number[]
  titleOnly?: boolean
  inPageIds?: string[]
  limit: number
}) {
  const { db, mode, limit } = args
  // Rank the eligible corpus before applying the final result limit.
  const vectorLimit = sql`(SELECT count(*) FROM ${db.pages})`
  const query = toTursoFtsQuery(args.query, args.titleOnly)
  // UNION reaches each child once, including selected roots; links confer no membership.
  // Keep this restriction inside the lexical corpus, before candidate/final limits.
  const inPages = args.inPageIds?.length ? sql`${db.pages.id} IN (
    WITH RECURSIVE searchScope(pageId) AS (
      SELECT ${db.pages.id} FROM ${db.pages}
      WHERE ${db.pages.id.inArray(args.inPageIds)} AND ${db.pages.deletedAt.isNull()} AND ${usablePage(db.pages)}
      UNION
      SELECT r.toPageId FROM ${db.pageRelations} r JOIN searchScope p ON r.fromPageId=p.pageId
      WHERE r.type='child'
    ) SELECT pageId FROM searchScope
  )` : sql`1`
  // Apply the page-source eligibility rule before candidate ranks and limits.
  const searchable = sql`${db.pages.deletedAt.isNull()} AND ${db.pages.kind.inArray(embeddedKinds())}
    AND ${usablePage(db.pages)} AND ${inPages}`
  // Keep the FTS query in the first binding slot and reuse it: the engine
  // recognizes score/match as one search only when their parameter ids match.
  // The native FTS top-K collector breaks score ties by internal document order.
  // Materialize the matching corpus before ranking by page identity; truncating
  // inside FTS can give the same exact title rank 1 in one file and no rank in another.
  // The physical row count bounds the collector without its implicit million-hit cap.
  // FTS and embeddings index the pageSearch projection; candidates keep pages.rowid as their identity.
  // fts_score/fts_match plan only on the indexed table alone, so the scored hits are a subquery joined to pages.
  const lexicalHits = sql`SELECT pages.rowid AS rowid, ${db.pages.id} AS pageId, hits.score AS score
    FROM (SELECT id, fts_score(title, text, ${query}) AS score FROM ${db.pageSearch} WHERE fts_match(title, text, ?1)
      ORDER BY score DESC LIMIT (SELECT count(*) FROM ${db.pageSearch})) hits
    JOIN ${db.pages} ON ${db.pages.id} = hits.id
    WHERE ${searchable}
    ORDER BY score DESC LIMIT (SELECT count(*) FROM ${db.pages})`
  const lexical = sql`SELECT rowid, row_number() OVER (ORDER BY score DESC, pageId) AS rank
    FROM lexicalHits`
  const vector = mode === 'fulltext' ? undefined
    : args.vector
  const index = args.index ?? FLASHCARDS_EMBEDDING_INDEX
  const queryVector = index.vectorFn === 'vector32' ? sql`vector32(${vector})` : sql`vector8(${vector})`
  const semantic = sql`SELECT pages.rowid AS rowid, row_number() OVER (ORDER BY e.distance, pages.rowid) AS rank
    FROM embedding_search(${index.name}, ${queryVector}, ${vectorLimit}) e
    JOIN ${db.pageSearch} ON pageSearch.rowid = e.rowid JOIN ${db.pages} ON ${db.pages.id} = pageSearch.id
    WHERE ${searchable}`
  const extraVector = mode !== 'fulltext' && args.nemotronVector ? encodeEmbeddingVector(args.nemotronVector) : undefined
  const extra = extraVector !== undefined
  const semanticNemotron = extra ? sql`SELECT pages.rowid AS rowid, row_number() OVER (ORDER BY e.distance, pages.rowid) AS rank
    FROM embedding_search(${NEMOTRON_EMBEDDING_INDEX.name}, vector32(${extraVector}), ${vectorLimit}) e
    JOIN ${db.pageSearch} ON pageSearch.rowid = e.rowid JOIN ${db.pages} ON ${db.pages.id} = pageSearch.id
    WHERE ${searchable}` : undefined
  const localCandidates = mode === 'fulltext' ? lexical : mode === 'vector' ? semantic
    : sql`SELECT rowid, rank FROM lexical UNION ALL SELECT rowid, rank FROM semantic`
  const candidates = extra ? (mode === 'vector'
    ? sql`SELECT rowid, rank FROM semantic UNION ALL SELECT rowid, rank FROM semanticNemotron`
    : sql`${localCandidates} UNION ALL SELECT rowid, rank FROM semanticNemotron`) : localCandidates
  const localCtes = mode === 'vector' ? sql.raw``
    : mode === 'fulltext' ? sql`lexicalHits AS MATERIALIZED (${lexicalHits}),`
    : sql`lexicalHits AS MATERIALIZED (${lexicalHits}), lexical AS (${lexical}), semantic AS MATERIALIZED (${semantic}),`
  const ctes = extra ? (mode === 'vector'
    ? sql`semantic AS MATERIALIZED (${semantic}), semanticNemotron AS MATERIALIZED (${semanticNemotron}),`
    : sql`${localCtes} semanticNemotron AS MATERIALIZED (${semanticNemotron}),`) : localCtes
  const exactTitle = sql`${db.pages.titleKey} IN (${searchKey(args.query)}, ${searchKey(args.query) + '.md'})`
  const exactRow = sql`${db.pages.kind.eq('databaseRow')} AND ${db.pages.deletedAt.isNull()}
    AND ${usablePage(db.pages)} AND ${exactTitle} AND ${inPages}`
  const ranked = queryOptions(db.query`WITH ${ctes} candidates AS (${candidates}),
    fused AS MATERIALIZED (SELECT rowid, sum(1.0 / (60 + rank)) AS score FROM candidates GROUP BY rowid)
    SELECT ${db.pages.id} AS pageId, ${db.pages.title}, coalesce(fused.score, 0.0) AS score
    FROM ${db.pages} LEFT JOIN fused ON pages.rowid = fused.rowid
    WHERE (${searchable} OR (${exactRow}))
      AND (fused.rowid IS NOT NULL OR ${exactTitle})
    ORDER BY ${exactTitle} DESC, score DESC, ${db.pages.id} LIMIT ${limit}`,
    scoredPageSchema,
  )
  return { ...ranked,
    async queryFn(qctx?: { tel?: Tel }) {
      if (!query || limit <= 0) return []
      const rows = await ranked.queryFn(qctx)
      return rows.toSorted((left, right) => compareSearchHits(left, right, args.query))
    },
  }
}

export function compareSearchHits(left: PageSearchResult & { spaceId?: string }, right: PageSearchResult & { spaceId?: string }, query: string) {
  const leftExact = normalizeTitle(left.title) === normalizeTitle(query)
  const rightExact = normalizeTitle(right.title) === normalizeTitle(query)
  if (leftExact !== rightExact) return leftExact ? -1 : 1
  return right.score - left.score || compareId(left.spaceId ?? '', right.spaceId ?? '') || compareId(left.pageId, right.pageId)
}
function compareId(left: string, right: string) { return left < right ? -1 : left > right ? 1 : 0 }

/** Last assigned alphanumeric code point (CJK Extension H, U+323AF); no title token character sorts above it. */
const HIGHEST_TOKEN_CHARACTER = String.fromCodePoint(0x32_3A_F)

// Letters/digits are matched by Unicode class, not by the ASCII ranges: study
// languages include Cyrillic, and an ASCII-only split erased such queries
// entirely, so every non-Latin search silently returned no pages.
export function toTursoFtsQuery(value: string, titleOnly = false): string {
  // Only our ranges/operators are syntax; typed words are quoted literals.
  const terms = value
    .split(/[^\p{L}\p{N}_]+/u)
    .map((item) => item.trim())
    .filter(Boolean)
    .map(item => JSON.stringify(item))
    .join(' ')
  // A term range searches the indexed title vocabulary for a typed prefix.
  // Bare `term*` is tokenized as a term; phrase-prefix syntax requires two terms.
  const prefixes = value.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []
  // The bound is tokenized like a title: incrementing the last character can yield a
  // separator ('z'→'{', '9'→':'), which empties the range or fails to parse. Close the
  // range inclusively at the prefix followed by the highest alphanumeric code point.
  const title = prefixes.map(prefix => `title:[${prefix} TO ${prefix}${HIGHEST_TOKEN_CHARACTER}]`).join(' AND ')
  const matches = titleOnly && terms ? `title:(${terms})` : terms
  return title ? `(${matches}) OR (${title})` : matches
}

function normalizeTitle(value: string): string {
  return searchKey(value).replace(/\.md$/iu, '')
}
